import { AuthProviderType } from "@elrond-giants/erdjs-auth/dist/types";
import { Account, Village } from "@elrond-giants/game-db/types/entities";
import axios from "axios";
import { useRouter } from "next/router";
import QRCode from "qrcode";
import { useEffect, useState } from "react";

import { buildWallet, createWallet, encryptPKey } from "../auth/create-wallet";
import { PasswordInputType } from "../common/types/components";
import { AuthResponse } from "../common/types/responses";
import LoadingScreen from "../components/LoadingScreen";
import NotAllowedPopup from "../components/NotAllowedPopup";
import ActionButton from "../components/shared/ActionButton";
import Popup from "../components/shared/Popup";
import { walletConnectDeepLink } from "../config";
import InvalidWalletPassword from "../errors/InvalidWalletPassword";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../hooks/useAuth";
import { usePasswordInput } from "../hooks/usePasswordInput";
import { getAuthClient } from "../utils/clientAuth";
import { clearReferralCode, getReferralCode } from "../utils/referrals";

export default function Auth() {
  const { env, setState, mainWallet } = useAuth();
  const { api } = useApi();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const { getPassword, PasswordInput, visible: passwordInputVisible } = usePasswordInput();
  const [maiarAuthUri, setMaiarAuthUri] = useState("");
  const [authQrCode, setAuthQrCode] = useState("");
  const [showAllowlistPopup, setShowAllowlistPopup] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [isExtensionDisabled, setIsExtensionDisabled] = useState(true);

  useEffect(() => {
    if (window !== undefined) {
      const params = new URLSearchParams(window.location.search);
      const accessToken = params.get("accessToken");
      if (accessToken) {
        (async () => {
          setLoading(true);

          await mainWallet!.login(AuthProviderType.WEBVIEW, accessToken, (address: string, signature: string) => {
            login(address, signature, accessToken);
          });
        })();
      }
    }
  }, []);

  const initAuth = async (providerType: AuthProviderType) => {
    setLoading(true);
    const authClient = getAuthClient();
    const initToken = await authClient.initialize();

    const res = await mainWallet!.login(providerType, initToken, (address: string, signature: string) => {
      const accessToken = authClient.getToken(address, initToken, signature);
      login(address, signature, accessToken);
    });

    if (providerType === AuthProviderType.WALLET_CONNECT) {
      const qrCode = await QRCode.toString(res, { type: "svg" });
      const authUri = `${walletConnectDeepLink}?wallet-connect=${encodeURIComponent(res)}`;
      setAuthQrCode(qrCode);
      setMaiarAuthUri(authUri);
    }
  };

  const login = async (address: string, signature: string, authToken: string) => {
    setAuthQrCode("");
    const {
      data: { allowed, maintenance },
    } = await api.get("auth/allowlist", { params: { address } });
    if (!allowed) {
      setMaintenance(maintenance);
      setShowAllowlistPopup(true);
      return;
    }
    try {
      const {
        data: { account, token, village },
      } = await api.post<AuthResponse>("auth/login", {
        address,
        signature,
        authToken,
      });
      await getPasswordAndLogin(account, token, village);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        await register(address, signature, authToken);
      }
    }
  };

  const register = async (address: string, signature: string, authToken: string) => {
    const {
      data: { allowed, maintenance },
    } = await api.get("auth/allowlist", { params: { address } });
    if (!allowed) {
      setMaintenance(maintenance);
      setShowAllowlistPopup(true);
      return;
    }
    const password = await getPassword(PasswordInputType.REGISTER);
    const { address: gameAddress, mnemonic } = await createWallet();
    const gameKey = await encryptPKey(mnemonic, password);
    const referral = getReferralCode();
    const {
      data: { account, token, village },
    } = await api.post<AuthResponse>("auth/register", {
      address,
      signature,
      authToken,
      gameAddress,
      gameKey,
      referralCode: referral,
    });
    if (referral) {
      clearReferralCode();
    }
    await doAuth(account, password, token, village);
  };

  const getPasswordAndLogin = async (account: Account, token: string, village: Village, failedAttempts: number = 0) => {
    if (failedAttempts >= 3) {
      router.reload();
      return;
    }
    const password = await getPassword(PasswordInputType.LOGIN, failedAttempts);
    try {
      await doAuth(account, password, token, village);
    } catch (e) {
      if (e instanceof InvalidWalletPassword) {
        await getPasswordAndLogin(account, token, village, failedAttempts + 1);
      }
    }
  };

  const doAuth = async (account: Account, password: string, token: string, village: Village) => {
    const wallet = await buildWallet(account.game_wallet_key!, password, env);
    await setState(account, wallet, token, village);
    await router.push("/");
  };

  const platform = require("platform");
  const isMobile = platform?.os?.family === "iOS" || platform?.os?.family === "Android";

  useEffect(() => {
    if (window?.elrondWallet) setIsExtensionDisabled(false);
  }, []);

  if (loading)
    return (
      <>
        <Popup
          open={!!authQrCode && !passwordInputVisible}
          setOpen={() => {}}
          closeOnOutsideClick={false}
          closeButton={false}
          allowFullScreen={false}
        >
          <div className="flex flex-col items-center space-y-4 p-4">
            <h2 className="text-xl text-theme-text">Login with xPortal</h2>
            <div className="w-60 h-60 " dangerouslySetInnerHTML={{ __html: authQrCode }} />
            <p className="mt-2 text-lg">Scan the QR code using xPortal</p>
            {isMobile ? (
              <a
                href={maiarAuthUri}
                className="mt-6 inline-flex items-center px-2.5 py-1.5 border border-secondary shadow-sm text-xs font-medium text-gray-700 bg-secondary-lightest hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-secondary-lightest"
                rel="noopener noreferrer nofollow"
                target="_blank"
              >
                Login with xPortal App
              </a>
            ) : null}
          </div>
        </Popup>
        <LoadingScreen />
        <PasswordInput />
        <NotAllowedPopup open={showAllowlistPopup} setOpen={setShowAllowlistPopup} maintenance={maintenance} />
      </>
    );

  return (
    <>
      <div className="flex sm:h-screen">
        <div
          className="flex-1 hidden sm:block bg-cover bg-center relative"
          style={{
            backgroundImage: "url(/auth/left-image.jpg)",
          }}
        >
          <div className="absolute top-0 left-0 w-full h-full bg-[#040932] opacity-60"></div>
          <div className="absolute top-0 left-0 w-full h-full z-10 p-20 flex flex-col items-start justify-between">
            <img src="/giants-village-logo.svg" alt="Giants" className="h-32" />

            <p className="text-white font-bold text-6xl max-w-xl leading-tight">
              Get ready for a <span className="text-secondary">magical experience</span>
            </p>
          </div>
        </div>

        <div className="flex-1 bg-white flex items-center justify-center h-screen">
          <div className="max-w-lg">
            <img src="/giants-village-logo.svg" alt="Giants" className="h-16 mx-auto mb-8 sm:hidden" />
            <h1 className="text-3xl sm:text-4xl text-[#040932] font-semibold mb-4 text-center">Connect your wallet</h1>
            <p className="text-gray-500 mb-10 text-center text-sm">
              Pick a login method to access the Giants Village game 🌾
            </p>
            <div className="flex flex-col max-w-xs mx-auto space-y-3 mb-24">
              <ActionButton onClick={() => initAuth(AuthProviderType.WALLET_CONNECT)} style="secondary">
                📱 xPortal
              </ActionButton>
              <ActionButton
                onClick={() =>
                  isExtensionDisabled
                    ? window.open(
                        "https://chromewebstore.google.com/detail/multiversx-defi-wallet/dngmlblcodfobpdpecaadgfbcggfjfnm",
                        "_blank"
                      )
                    : initAuth(AuthProviderType.EXTENSION)
                }
                style="secondary"
                disabled={isMobile}
              >
                💻 Extension
              </ActionButton>
              {/*<p className="text-xs text-gray-400 text-center">* To use Ledger, please select Web Wallet.</p>*/}
            </div>

            <div>
              <p className="text-xl font-semibold text-[#040932] text-center">New to MultiversX?</p>
              <p className="text-gray-600 text-sm text-center">
                Easily create your own MultiversX wallet using{" "}
                <a
                  href="https://maiar.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-secondary hover:underline"
                >
                  xPortal
                </a>
              </p>
            </div>
          </div>
        </div>

        {authQrCode && <div className="w-60 h-60 mt-6" dangerouslySetInnerHTML={{ __html: authQrCode }} />}
      </div>
      <PasswordInput />
    </>
  );
}
